import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import Coach from "./components/chat/Coach";
import AdminCoach from "./components/chat/AdminCoach";
import PrivacyPolicy from "./components/pages/PrivacyPolicy";
import DeleteAccount from "./components/pages/DeleteAccount";
import StaticLanding from "./components/pages/StaticLanding";
import ConversationalLanding from "./components/pages/ConversationalLanding";
import NewLanding from "./components/pages/landing/Index";
import UserInitializationHandler from "./components/handlers/UserInitializationHandler";
import ResolutionBuilder from "./components/new-years/ResolutionHelper";
import {
  initAnalytics,
  identify as analyticsIdentify,
  track,
  EVENTS,
} from "./utils/analytics";
import RouteTracker from "./components/RouteTracker";
import SalesLanding from "./components/sales-agent/SalesLanding";
import Science from "./components/pages/Science";
import BetaLanding from "./components/pages/BetaLanding";
import PurposeAssessment from "./purpose-assessment/PurposeAssessment";
import Onboarding from "./components/onboarding/Onboarding";
import OnboardingV2 from "./components/onboarding/OnboardingV2";
import Connect from "./components/Connect";
import TextingConsent from "./components/Texting";
import Landing from "./components/sales-agent/Landing";
import CoachLanding from "./components/coach/landing/BasicLanding";
import CoachRoutes from "./CoachRoutes";
import Subscribe from "./components/Subscribe";
import AdminData from "./components/admin/AdminData";
import StrideLandingPage from "./stride/landing";
import CareerPlanner from "./stride/planning_tool";
import CareerDashboard from "./stride/user_dashboard";
import PanelLanding from "./panel/landing";
import HirLanding from "./components/pages/HirLanding";
import HirLanding2 from "./components/pages/HirLanding2";
import GroupCoachingLanding from "./components/pages/GroupCoachingLanding";
import AboutPanelPage from "./panel/about";
import MainLanding from "./components/main-landing/Index";
import { LifeScoreAssessment } from "./assessments";
import posthog from "posthog-js";
import Connected from "./components/pages/Connected";
// Initialise analytics ONCE at module load, before any component renders, so
// no event can be dropped because init had not run yet. Previously this lived
// inside the App() body and was re-executed on every render, and was ALSO
// duplicated in ~16 other components with conflicting configs.
initAnalytics();

function App() {
  const [userId, setUserId] = useState(localStorage.getItem("userId") || false);
  const [referrerId, setReferrerId] = useState(
    localStorage.getItem("referrerId") || false
  );

  useEffect(() => {
    if (userId) {
      localStorage.setItem("userId", userId);
      analyticsIdentify(userId, { referrer_id: referrerId || null });
    }
    if (referrerId) localStorage.setItem("referrerId", referrerId);
  }, [userId, referrerId]);

  // One App Loaded event per browser session (pageviews are handled by
  // <RouteTracker/>, so this is app boot only, not navigation).
  useEffect(() => {
    if (!sessionStorage.getItem("1440_app_loaded_tracked")) {
      sessionStorage.setItem("1440_app_loaded_tracked", "true");
      track(EVENTS.APP_LOADED, {
        is_authenticated: !!localStorage.getItem("1440_access_token"),
        entry_path: window.location.pathname,
      });
    }
  }, []);

  const handleReferrerUpdate = (newReferrerId) => {
    setReferrerId(newReferrerId);
  };

  return (
    <Router>
      {/* Emits exactly one "Page Viewed" per client-side route change. */}
      <RouteTracker />
      <Routes>
        <Route path="/connect/:phone" element={<Coach userId={userId} />} />

        <Route
          path="/admin/connect/:phone"
          element={<AdminCoach userId={userId} />}
        />

        <Route path="/texting" element={<TextingConsent />} />

        {/* Additional Routes */}
        <Route
          path="/privacy-policy"
          element={<PrivacyPolicy userId={userId} />}
        />
        <Route path="/" element={<CoachLanding />} />
        <Route path="/connected" element={<Connected />} />
        <Route
          path="/delete-account"
          element={<DeleteAccount userId={userId} />}
        />
        <Route path="/about" element={<StaticLanding userId={userId} />} />
        <Route path="/science" element={<Science userId={userId} />} />
        <Route
          path="/purpose"
          element={<PurposeAssessment userId={userId} />}
        />
        <Route path="/beta" element={<BetaLanding userId={userId} />} />
        <Route path="/onboarding" element={<Onboarding userId={userId} />} />
        <Route path="/onboard" element={<OnboardingV2 />} />

        <Route path="/welcome" element={<NewLanding />} />
        <Route path="/main" element={<MainLanding />} />
        <Route path="/life-score" element={<LifeScoreAssessment />} />
        <Route path="/life-score/:userId" element={<LifeScoreAssessment />} />
        <Route path="/wheel-of-life" element={<LifeScoreAssessment />} />
        <Route
          path="/wheel-of-life/:userId"
          element={<LifeScoreAssessment />}
        />

        <Route path="/admin_data" element={<AdminData />} />
        <Route path="/admin" element={<AdminData />} />

        <Route
          path="/landing"
          element={<Landing userId={userId} setUserId={setUserId} />}
        />
        <Route
          path="/resolutions"
          element={<ResolutionBuilder userId={userId} setUserId={setUserId} />}
        />

        <Route path="/hir" element={<HirLanding2 />} />
        <Route path="/hir2" element={<HirLanding />} />

        {/* Route for Coach */}
        <Route path="/user" element={<Coach />} />
        <Route path="/connect" element={<Coach />} />
        <Route path="/coach/*" element={<CoachRoutes />} />

        <Route
          path="/coach"
          element={
            <UserInitializationHandler
              userId={userId}
              setUserId={setUserId}
              referrerId={referrerId}
            />
          }
        />
        <Route path="/stride" element={<StrideLandingPage />} />
        <Route
          path="/groups/:userId"
          element={<GroupCoachingLanding userId={userId} />}
        />
        <Route path="/career_planner" element={<CareerPlanner />} />

        <Route path="/stride_dashboard" element={<CareerDashboard />} />
        <Route path="/stride_dashboard" element={<CareerDashboard />} />

        <Route path="/panel" element={<PanelLanding />} />
        <Route path="/panel/about" element={<AboutPanelPage />} />
      </Routes>
    </Router>
  );
}

export default App;
